import { API_BASE_URL } from './baseUrl';
import { resolveErrorMessage } from '../utils/errorMessage';

// 通用请求函数
const request = async (url, options = {}) => {
  const defaultOptions = {
    method: 'GET',
    mode: 'cors',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  // 如果是FormData，移除Content-Type让浏览器自动设置
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      let detailText = '';
      let detailJson = null;
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          detailJson = await response.json();
        } else if (!contentType.includes('text/html')) {
          detailText = await response.text();
        }
      } catch (_) {
        // ignore
      }
      const rawMessage = detailJson?.error_message
        || detailJson?.message
        || detailJson?.detail
        || detailText
        || '';
      const fallback = `请求失败（HTTP ${response.status}）`;
      const friendly = resolveErrorMessage(rawMessage, fallback);
      const err = new Error(friendly);
      err.status = response.status;
      err.detail = detailJson || detailText;
      err.url = url;
      throw err;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

const postStatistics = async (path, payload = {}) => request(`${API_BASE_URL}${path}`, {
  method: 'POST',
  body: JSON.stringify(payload),
});

// 数据统计（阶段A：生产 + 发布）
export const getStatisticsFilterOptions = async (payload = {}) => postStatistics('/statistics/filters/options', payload);
export const getProductionStatisticsOverview = async (payload = {}) => postStatistics('/statistics/production/overview', payload);
export const getProductionStatisticsQuery = async (payload = {}) => postStatistics('/statistics/production/query', payload);
export const getPublishStatisticsOverview = async (payload = {}) => postStatistics('/statistics/publish/overview', payload);
export const getPublishStatisticsQuery = async (payload = {}) => postStatistics('/statistics/publish/query', payload);
export const getPublishFailureReasons = async (payload = {}) => postStatistics('/statistics/publish/failure-reasons', payload);
export const getEffectStatisticsOverview = async (payload = {}) => postStatistics('/statistics/effect/overview', payload);
export const getEffectStatisticsQuery = async (payload = {}) => postStatistics('/statistics/effect/query', payload);
export const syncEffectStatistics = async (payload = {}) => postStatistics('/statistics/effect/sync', payload);
export const collectXhsEffectStatistics = async (payload = {}) => postStatistics('/statistics/effect/collect/xhs', payload);
export const createEffectCollectTask = async (payload = {}) => postStatistics('/statistics/effect/tasks/create', payload);
export const listEffectCollectTasks = async (payload = {}) => postStatistics('/statistics/effect/tasks/list', payload);
export const updateEffectCollectTask = async (payload = {}) => postStatistics('/statistics/effect/tasks/update', payload);
export const deleteEffectCollectTask = async (payload = {}) => postStatistics('/statistics/effect/tasks/delete', payload);
export const triggerEffectCollectTaskOnce = async (payload = {}) => postStatistics('/statistics/effect/tasks/trigger_once', payload);
export const getActiveEffectCollectTaskRuns = async (payload = {}) => postStatistics('/statistics/effect/tasks/active_runs', payload);

// 内容上传接口
export const uploadContent = async (formData) => {
  const url = `${API_BASE_URL}/content/upload`;

  // 如果是 FormData，不需要设置 Content-Type，浏览器会自动设置
  const options = formData instanceof FormData
    ? {
      method: 'POST',
      body: formData,
    }
    : {
      method: 'POST',
      body: JSON.stringify(formData),
    };

  return request(url, options);
};

// 内容元数据预生成（不上传、不入库；用于上传前 AI 生成 title/summary/tags）
export const previewContentMetadata = async (formData) => {
  const url = `${API_BASE_URL}/content/preview_metadata`;
  const options = {
    method: 'POST',
    body: formData,
  };
  return request(url, options);
};

// 小红书笔记：基于正文 AI 生成简介（不入库）
export const previewXhsNoteSummary = async (params = {}) => {
  const url = `${API_BASE_URL}/content/preview_xhs_note_summary`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 小红书笔记：基于正文 AI 生成标签（不入库）
export const previewXhsNoteTags = async (params = {}) => {
  const url = `${API_BASE_URL}/content/preview_xhs_note_tags`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 小红书笔记：新建（多图+正文写入内容库）
export const createXhsNote = async (formData) => {
  const url = `${API_BASE_URL}/content/create_xhs_note`;
  return request(url, {
    method: 'POST',
    body: formData,
  });
};

// 内容查询接口
export const getContentList = async (params = {}) => {
  const url = `${API_BASE_URL}/content/list`;

  const defaultParams = {
    page: 1,
    page_size: 20,
    sort_field: 'ingested_at',
    sort_order: '降序',
    ...params,
  };

  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

// 内容检索接口
export const searchContent = async (searchParams = {}) => {
  const url = `${API_BASE_URL}/content/search`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(searchParams),
  });
};

// 内容删除接口
export const deleteContent = async (searchParams = {}) => {
  const url = `${API_BASE_URL}/content/delete`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(searchParams),
  });
};

// 内容下载接口
export const downloadContent = async (contentId) => {
  const url = `${API_BASE_URL}/content/download`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content_id: contentId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // 检查响应类型
    const contentType = response.headers.get('content-type');

    // 如果是文件流（blob），处理文件下载
    if (contentType && (contentType.includes('application/octet-stream') || contentType.includes('application/zip') || contentType.includes('application/pdf') || contentType.includes('video/') || contentType.includes('image/'))) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `content_${contentId}`;

      // 从 Content-Disposition 头中提取文件名
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // 处理 UTF-8 编码的文件名
          if (filename.startsWith('UTF-8\'\'')) {
            filename = decodeURIComponent(filename.replace(/^UTF-8''/, ''));
          }
        }
      }

      // 创建下载链接并触发下载
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { status: 'success', message: '下载成功' };
    } else {
      // 如果是 JSON 响应，解析并返回
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.error('下载失败:', error);
    throw error;
  }
};

//内容详情接口
export const getContentDetail = async (searchParams = {}) => {
  const url = `${API_BASE_URL}/content/detail`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(searchParams),
  });
};


//内容修改接口
export const modifyContentDetail = async (searchParams = {}) => {
  const url = `${API_BASE_URL}/content/update`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(searchParams),
  });
};

// 搜索发布账号接口
export const searchPublishAccounts = async (searchParams = {}) => {
  const url = `${API_BASE_URL}/accounts/search`;

  // 平台代码 -> 中文名称（accounts/search 当前后端以中文枚举为准）
  const platformNameMap = {
    wechat: '微信公众号',
    wechat_channels: '微信视频号',
    xhs: '小红书',
    douyin: '抖音',
    kuaishou: '快手',
    baijiahao: '百家号',
    zhihu: '知乎',
    bilibili: 'B站',
    toutiao: '今日头条',
  };

  const defaultParams = {
    page: 1,
    page_size: 50,
    keyword: '',
    filters: {},
    sort: { field: 'created_at', order: 'desc' },
    ...searchParams,
  };

  // 兼容前端使用英文代码的情况：如 wechat_channels -> 微信视频号
  if (defaultParams.filters && defaultParams.filters.platforms) {
    const platforms = Array.isArray(defaultParams.filters.platforms)
      ? defaultParams.filters.platforms
      : [defaultParams.filters.platforms];
    defaultParams.filters = {
      ...defaultParams.filters,
      platforms: platforms.map((p) => platformNameMap[p] || p),
    };
  }

  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

// 发布账号详情接口
export const getPublishAccountDetail = async (accountIdOrParams) => {
  const url = `${API_BASE_URL}/accounts/detail`;

  const params = typeof accountIdOrParams === 'string'
    ? { account_id: accountIdOrParams }
    : (accountIdOrParams ?? {});

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 创建发布账号接口
export const createPublishAccount = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/create`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 更新发布账号接口
export const updatePublishAccount = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/update`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 删除发布账号接口（支持传单个 ID / ID 数组 / 原始 params）
export const deletePublishAccounts = async (accountIdsOrParams) => {
  const url = `${API_BASE_URL}/accounts/delete`;

  let params;
  if (typeof accountIdsOrParams === 'string') {
    params = { account_id: accountIdsOrParams };
  } else if (Array.isArray(accountIdsOrParams)) {
    params = { account_ids: accountIdsOrParams };
  } else {
    params = accountIdsOrParams ?? {};
  }

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// Agent 列表
export const listAgents = async (params = {}) => {
  const url = `${API_BASE_URL}/agents/list`;
  const defaultParams = {
    page: 1,
    page_size: 100,
    online_window_sec: 60,
    ...params,
  };
  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

// 账号-设备会话列表
export const listAccountAgentSessions = async (params = {}) => {
  const url = `${API_BASE_URL}/agents/accounts/sessions/list`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 派发预检：校验账号是否可在指定 Agent 上执行
export const previewAgentDispatch = async (params = {}, options = {}) => {
  const url = `${API_BASE_URL}/agents/dispatch/preview`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
    signal: options.signal,
  });
};

// 账号绑定/解绑 Agent
export const bindAgentAccount = async (params = {}) => {
  const url = `${API_BASE_URL}/agents/accounts/bind`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 认领本机 Agent（将 agent 归属到当前登录用户）
export const claimAgent = async (params = {}) => {
  const url = `${API_BASE_URL}/agents/claim`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 删除 Agent（用于清理长期离线/不可达设备）
export const deleteAgent = async (params = {}) => {
  const url = `${API_BASE_URL}/agents/delete`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 小红书：查询登录状态（按账号实例）
export const xhsAccountLoginStatus = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/xhs/login/status`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 小红书：获取登录二维码（按账号实例）
export const xhsAccountLoginQrcode = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/xhs/login/qrcode`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 小红书：上传 cookies 到 OSS 并回写账号配置（可选重启 MCP）
export const xhsAccountPersistCookies = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/xhs/cookies/persist`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const xhsMcpEnsure = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/xhs/mcp/ensure`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 社交平台（抖音/快手/微信视频号等）：macOS 本机扫码登录（Playwright headed）
export const socialAccountLoginStart = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/social/login/start`;
  return request(url, { method: 'POST', body: JSON.stringify(params) });
};

export const socialAccountLoginQrcode = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/social/login/qrcode`;
  return request(url, { method: 'POST', body: JSON.stringify(params) });
};

export const socialAccountLoginStatus = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/social/login/status`;
  return request(url, { method: 'POST', body: JSON.stringify(params) });
};

export const socialAccountLoginSms = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/social/login/sms`;
  return request(url, { method: 'POST', body: JSON.stringify(params) });
};

export const socialAccountLoginFinish = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/social/login/finish`;
  return request(url, { method: 'POST', body: JSON.stringify(params) });
};

export const socialAccountLoginCancel = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/social/login/cancel`;
  return request(url, { method: 'POST', body: JSON.stringify(params) });
};

// 抖音：检测 cookies 是否有效（按账号实例）
export const douyinAccountCookieCheck = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/douyin/cookies/check`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 快手：检测 cookies 是否有效（按账号实例）
export const kuaishouAccountCookieCheck = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/kuaishou/cookies/check`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 微信视频号：检测 cookies 是否有效（按账号实例）
export const weixinChannelsAccountCookieCheck = async (params = {}) => {
  const url = `${API_BASE_URL}/accounts/weixin_channels/cookies/check`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 立即发布接口
export const executePublishNow = async (params = {}, options = {}) => {
  const url = `${API_BASE_URL}/publish/execute_now`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
    signal: options.signal,
  });
};

// 已发布内容：重试失败记录（原地更新 publish_id）
export const retryPublished = async (params = {}, options = {}) => {
  const url = `${API_BASE_URL}/publish/retry`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
    signal: options.signal,
  });
};

// 发布验证码信息
export const getPublishVerificationInfo = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/verification/info`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 提交短信验证码
export const submitPublishVerificationSms = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/verification/sms`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 取消短信验证码
export const cancelPublishVerification = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/verification/cancel`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 创建定时发布任务接口
export const createPublishTask = async (params = {}, options = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/create`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
    signal: options.signal,
  });
};

// 获取定时任务列表接口
export const getPublishTaskList = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/list`;

  const defaultParams = {
    page: 1,
    page_size: 20,
    keyword: '',
    filters: {},
    sort: { field: 'created_at', order: 'desc' },
    ...params,
  };

  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

// 更新定时任务接口
export const updatePublishTask = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/update`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 删除定时任务接口
export const deletePublishTask = async (taskIds) => {
  const url = `${API_BASE_URL}/publish/tasks/delete`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify({
      task_ids: Array.isArray(taskIds) ? taskIds : [taskIds],
    }),
  });
};

// 解析定时任务文本接口
export const nlToSchedule = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/nl_to_schedule`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 获取已发布内容列表接口
export const getPublishList = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/list`;

  const defaultParams = {
    page: 1,
    page_size: 10,
    sort_field: 'publish_time',
    sort_order: 'desc',
    ...params,
  };

  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

// 搜索已发布内容接口
export const searchPublishList = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/search`;

  const defaultParams = {
    page: 1,
    page_size: 20,
    keyword: '',
    filters: {},
    sort: { field: 'publish_time', order: 'desc' },
    ...params,
  };

  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

// 获取已发布内容详情接口
export const getPublishDetail = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/detail`;

  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// 下载已发布内容接口
export const downloadPublishContent = async (publishId) => {
  const url = `${API_BASE_URL}/publish/download`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        publish_id: publishId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // 检查响应类型
    const contentType = response.headers.get('content-type');

    // 如果是文件流（blob），处理文件下载
    if (contentType && (contentType.includes('application/octet-stream') ||
      contentType.includes('application/zip') ||
      contentType.includes('application/pdf') ||
      contentType.includes('video/') ||
      contentType.includes('image/') ||
      contentType.includes('text/'))) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `publish_${publishId}`;

      // 从 Content-Disposition 头中提取文件名
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // 处理 UTF-8 编码的文件名
          if (filename.startsWith('UTF-8\'\'')) {
            filename = decodeURIComponent(filename.replace(/^UTF-8''/, ''));
          }
        }
      }

      // 创建下载链接并触发下载
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { status: 'success', message: '下载成功' };
    } else {
      // 如果是 JSON 响应，解析并返回
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.error('下载失败:', error);
    throw error;
  }
};

// 删除已发布内容接口
export const deletePublishContent = async (publishId) => {
  return batchDeletePublishContent([publishId]);
};

// 批量删除已发布内容接口（后端支持 publish_ids）
export const batchDeletePublishContent = async (publishIds) => {
  const url = `${API_BASE_URL}/publish/delete`;

  const ids = Array.isArray(publishIds) ? publishIds : [publishIds];
  return request(url, {
    method: 'POST',
    body: JSON.stringify({
      publish_ids: ids,
    }),
  });
};

// 获取定时任务列表接口
export const getPublishTasksList = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/list`;

  const defaultParams = {
    page: 1,
    page_size: 20,
    keyword: '',
    filters: {},
    sort: { field: 'created_at', order: 'desc' },
    ...params,
  };

  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

// ---------------- 内容生产（producers） ----------------

export const getProducersRegistry = async () => {
  const url = `${API_BASE_URL}/producers/registry`;
  return request(url, { method: 'GET' });
};

export const runProducerGenerate = async (producerName, params = {}) => {
  const name = String(producerName || '').trim();
  if (!name) throw new Error('producerName is required');
  const url = `${API_BASE_URL}/producers/${encodeURIComponent(name)}/generate`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const optimizeIdeaToVideo = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/idea_to_video/optimize`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const uploadProducerTempImages = async (files = [], options = {}) => {
  const normalized = Array.isArray(files) ? files : [files];
  const blobs = normalized
    .map((item) => item?.originFileObj || item)
    .filter((item) => item instanceof File);

  if (blobs.length === 0) {
    return { status: 'success', items: [] };
  }

  const formData = new FormData();
  blobs.forEach((file) => formData.append('files', file));
  const category = String(options?.category || '').trim();
  if (category) formData.append('category', category);

  const url = `${API_BASE_URL}/producers/uploads/images/temp`;
  return request(url, {
    method: 'POST',
    body: formData,
  });
};

// Producer Runs (async + SSE)
export const createProducerRun = async (producerName, params = {}) => {
  const name = String(producerName || '').trim();
  if (!name) throw new Error('producerName is required');
  const url = `${API_BASE_URL}/producers/${encodeURIComponent(name)}/runs`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getProducerRun = async (runId) => {
  const id = String(runId || '').trim();
  if (!id) throw new Error('runId is required');
  const url = `${API_BASE_URL}/producers/runs/${encodeURIComponent(id)}`;
  return request(url, { method: 'GET' });
};

export const cancelProducerRun = async (runId) => {
  const id = String(runId || '').trim();
  if (!id) throw new Error('runId is required');
  const url = `${API_BASE_URL}/producers/runs/${encodeURIComponent(id)}/cancel`;
  return request(url, { method: 'POST' });
};

export const triggerProducerRun = async (runId) => {
  const id = String(runId || '').trim();
  if (!id) throw new Error('runId is required');
  const url = `${API_BASE_URL}/producers/runs/${encodeURIComponent(id)}/trigger`;
  return request(url, { method: 'POST' });
};

// Producer Schedules (定时生产)
export const createProducerSchedule = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/schedules/create`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const updateProducerSchedule = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/schedules/update`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const deleteProducerSchedules = async (scheduleIdsOrParams) => {
  const url = `${API_BASE_URL}/producers/schedules/delete`;
  let params;
  if (typeof scheduleIdsOrParams === 'string') {
    params = { schedule_id: scheduleIdsOrParams };
  } else if (Array.isArray(scheduleIdsOrParams)) {
    params = { schedule_ids: scheduleIdsOrParams };
  } else {
    params = scheduleIdsOrParams ?? {};
  }
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const listProducerSchedules = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/schedules/list`;
  const defaultParams = {
    page: 1,
    page_size: 20,
    ...params,
  };
  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

export const triggerProducerScheduleOnce = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/schedules/trigger_once`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getActiveProducerScheduleRuns = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/schedules/active_runs`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getProducerScheduleRunSummary = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/schedules/run_summary`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const listProducerRuns = async (params = {}) => {
  const url = `${API_BASE_URL}/producers/runs/list`;
  const defaultParams = { page: 1, page_size: 10, ...params };
  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

export const triggerPublishTaskOnce = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/trigger_once`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const executePublishTaskOnce = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/execute_once`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getActivePublishTaskRuns = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/tasks/active_runs`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getPublishTaskRun = async (taskRunId) => {
  const id = String(taskRunId || '').trim();
  if (!id) throw new Error('taskRunId is required');
  const url = `${API_BASE_URL}/publish/task_runs/${encodeURIComponent(id)}`;
  return request(url, { method: 'GET' });
};

export const listPublishJobsByRun = async (taskRunId, params = {}) => {
  const id = String(taskRunId || '').trim();
  if (!id) throw new Error('taskRunId is required');
  const url = `${API_BASE_URL}/publish/task_runs/${encodeURIComponent(id)}/jobs/list`;
  const defaultParams = { page: 1, page_size: 50, ...params };
  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

export const listPublishTaskRuns = async (params = {}) => {
  const url = `${API_BASE_URL}/publish/task_runs/list`;
  const defaultParams = { page: 1, page_size: 10, ...params };
  return request(url, {
    method: 'POST',
    body: JSON.stringify(defaultParams),
  });
};

export const cancelPublishTaskRun = async (taskRunId) => {
  const id = String(taskRunId || '').trim();
  if (!id) throw new Error('taskRunId is required');
  const url = `${API_BASE_URL}/publish/task_runs/${encodeURIComponent(id)}/cancel`;
  return request(url, { method: 'POST' });
};
